import { useMemo } from "react";
import { useQuery } from "urql";
import { useFragment } from "../../gql";
import { PlaylistFieldsFragment } from "../../gql/graphql";
import PlaylistData from "../data/PlaylistData";
import {
  playlistFieldsFragment,
  playlistsFullQuery,
} from "./playlistDocuments";
import { sortByPosition } from "./PlaylistItemOrder";

/** Items without a song (title or note only) are kept on the server, but the UI does not show them. */
export function toPlaylistData(playlist: PlaylistFieldsFragment): PlaylistData {
  return new PlaylistData(
    playlist.id,
    playlist.title,
    playlist.belongsToCrew ?? undefined,
    playlist.tags,
    sortByPosition(playlist.items).flatMap((item) =>
      item.songId
        ? [{ id: item.id, position: item.position, songEntryId: item.songId }]
        : [],
    ),
    new Date(playlist.createdAt),
    new Date(playlist.updatedAt),
    false,
  );
}

/**
 * All playlists the user can access, from the cache (filled by the sync at start). Re-renders on every change,
 * including optimistic ones. `undefined` while loading; an empty list if nothing is cached and the device is offline.
 */
export function usePlaylists(): readonly PlaylistData[] | undefined {
  const [{ data, error }] = useQuery({
    query: playlistsFullQuery,
    requestPolicy: "cache-first",
  });
  const fragments = useFragment(playlistFieldsFragment, data?.playlists);
  return useMemo(
    () => (fragments ? fragments.map(toPlaylistData) : error ? [] : undefined),
    [fragments, error],
  );
}

/** `undefined` while loading, `null` if there is no such playlist (deleted or not accessible). */
export function usePlaylist(id: string): PlaylistData | null | undefined {
  const playlists = usePlaylists();
  return useMemo(
    () =>
      playlists === undefined
        ? undefined
        : (playlists.find((p) => p.id === id) ?? null),
    [playlists, id],
  );
}
